<template>
  <q-card flat bordered>
    <q-card-section>
      <div class="section-header">
        <div class="text-h6">General Inclusions</div>
      </div>

      <q-linear-progress v-if="loadingLists || loadingItems" indeterminate color="primary" class="q-mt-md" />

      <div class="q-mt-md">
        <q-select
          v-model="selectedList"
          :options="listOptions"
          label="Select Inclusions List"
          outlined
          emit-value
          map-options
          clearable
          :loading="loadingLists"
          :disable="loadingLists || loadingItems"
          @update:model-value="onListSelected"
        >
          <template #prepend>
            <q-icon name="list" />
          </template>
        </q-select>
      </div>

      <div v-if="internalInclusions.length > 0" class="q-mt-md">
        <div v-for="category in categorizedInclusions" :key="category.name" class="category-section">
          <q-expansion-item
            :label="category.name"
            header-class="bg-grey-2 text-weight-medium"
            expand-separator
          >
            <q-list bordered separator>
              <q-item
                v-for="inclusion in category.items"
                :key="inclusion.id"
                tag="label"
              >
                <q-item-section avatar>
                  <q-checkbox
                    v-model="inclusion.is_included"
                    :disable="loadingItems"
                    @update:model-value="toggleInclusion(inclusion)"
                  />
                </q-item-section>

                <q-item-section>
                  <q-item-label>{{ inclusion.text || inclusion.item_text }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>
        </div>
      </div>

      <div v-else-if="!loadingLists && !loadingItems" class="empty-state q-pa-lg text-center">
        <q-icon name="checklist" size="48px" color="grey-5" />
        <p class="text-grey-7 q-mt-md">
          {{ selectedList ? 'No items in this list' : 'Select an inclusions list to get started' }}
        </p>
      </div>
    </q-card-section>
  </q-card>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
    selectedListId: {
      type: Number,
      default: null,
    },
    inclusions: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['list-selected', 'item-toggled']);

  const $q = useQuasar();

  // State
  const loadingLists = ref(false);
  const loadingItems = ref(false);
  const availableLists = ref([]);
  const selectedList = ref(props.selectedListId);
  const internalInclusions = ref([...props.inclusions]);

  // Computed
  const listOptions = computed(() => {
    return availableLists.value.map((list) => ({
      label: list.name,
      value: list.id,
    }));
  });

  const categorizedInclusions = computed(() => {
    const categories = new Map();

    // Group items by category
    internalInclusions.value.forEach((item) => {
      const categoryName = item.category || 'Uncategorized';
      if (!categories.has(categoryName)) {
        categories.set(categoryName, {
          name: categoryName,
          items: [],
        });
      }
      categories.get(categoryName).items.push(item);
    });

    // Convert to array and sort - Uncategorized first if exists, then alphabetically
    return Array.from(categories.values()).sort((a, b) => {
      if (a.name === 'Uncategorized') {return -1;}
      if (b.name === 'Uncategorized') {return 1;}
      return a.name.localeCompare(b.name);
    });
  });

  // Watch for prop changes
  watch(() => props.selectedListId, (newValue) => {
    selectedList.value = newValue;
  });

  watch(() => props.inclusions, (newInclusions) => {
    internalInclusions.value = [...newInclusions];
  }, { deep: true });

  // Methods
  async function loadAvailableLists() {
    loadingLists.value = true;
    try {
      const response = await authenticatedFetch('/api/settings/general-inclusions-lists');
      const data = await response.json();
      availableLists.value = data.success ? data.data : data;
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to load lists: ' + error.message,
          timeout: 4000,
        });
      }
    } finally {
      loadingLists.value = false;
    }
  }

  async function onListSelected(listId) {
    if (!listId) {
      internalInclusions.value = [];
      emit('list-selected', null);
      return;
    }

    loadingItems.value = true;
    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/select-inclusions-list`, {
        method: 'POST',
        body: JSON.stringify({ list_id: listId }),
      });

      const data = await response.json();

      if (data.success) {
        internalInclusions.value = data.data.general_inclusions || [];

        $q.notify({
          type: 'positive',
          message: 'Inclusions list selected successfully',
          timeout: 2000,
        });

        emit('list-selected', data.data);
      }
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to select list: ' + error.message,
          timeout: 4000,
        });
      }
    } finally {
      loadingItems.value = false;
    }
  }

  async function toggleInclusion(inclusion) {
    const previousState = !inclusion.is_included;

    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/toggle-inclusion/${inclusion.id}`, {
        method: 'PUT',
      });

      const data = await response.json();

      if (data.success) {
        emit('item-toggled', data.data);
      }
    } catch (error) {
      // Revert on error
      inclusion.is_included = previousState;

      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to update inclusion: ' + error.message,
          timeout: 4000,
        });
      }
    }
  }

  // Lifecycle
  onMounted(() => {
    loadAvailableLists();
  });
</script>

<style scoped>
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.empty-state {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.category-section {
  margin-bottom: 12px;
}

.category-section:last-child {
  margin-bottom: 0;
}
</style>
